import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Paper,
    Grid,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
} from "@mui/material";
import {
    Error as ErrorIcon,
    CheckCircle,
    Create,
    GroupAdd,
    Info,
    RadioButtonChecked,
    RadioButtonUnchecked,
} from "@mui/icons-material";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";
import { getTotalParameterCount, analyzeTemplateParameters } from "../../../utils/templateParameterUtils";
import { useNavigate } from "react-router-dom";

const GroupSelectionStep = ({
    selectedGroup,
    onGroupSelect,
    templateDetails,
    parameterSource,
    onParameterSourceChange,
    customParameters,
    onCustomParametersChange,
}) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const notify = useNotification();
    const navigate = useNavigate();

    // Group data state
    const [groupData, setGroupData] = useState(null);
    const [loadingGroupData, setLoadingGroupData] = useState(false);
    const [parameterMismatch, setParameterMismatch] = useState(null);
    const [commonParameters, setCommonParameters] = useState([]);
    const [showParameterDialog, setShowParameterDialog] = useState(false);

    // Use props if provided, otherwise use local state
    const [localParameterSource, setLocalParameterSource] = useState(parameterSource || "group");
    const [localCustomParameters, setLocalCustomParameters] = useState(customParameters || []);

    // Use props if available, otherwise use local state
    const currentParameterSource = parameterSource !== undefined ? parameterSource : localParameterSource;
    const currentCustomParameters = customParameters !== undefined ? customParameters : localCustomParameters;

    useEffect(() => {
        fetchGroups();
    }, []);

    useEffect(() => {
        if (selectedGroup && templateDetails) {
            fetchGroupData();
        }
    }, [selectedGroup, templateDetails]);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/groups`
            );
            if (response.data?.success && response.data?.data) {
                setGroups(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching groups:", error);
            notify("Failed to fetch groups", "error");
        } finally {
            setLoading(false);
        }
    };


    const fetchGroupData = async () => {
        if (!selectedGroup || !templateDetails) return;

        setLoadingGroupData(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/groups/${selectedGroup}/data`
            );

            if (response.data?.success) {
                setGroupData(response.data);
                
                // Check parameter mismatch using new format
                const templateParamCount = getTotalParameterCount(templateDetails);
                const groupParamCount = response.data.number_of_parameters || 0;
                
                // Extract header and body param counts from new format
                const groupHeaderParamCount = response.data.header_param_count || 0;
                const groupBodyParamCount = response.data.body_param_count || 0;

                // Initialize custom parameters based on template requirements
                if (templateParamCount > 0) {
                    const newCustomParams = new Array(templateParamCount).fill("");
                    if (onCustomParametersChange) {
                        onCustomParametersChange(newCustomParams);
                    } else {
                        setLocalCustomParameters(newCustomParams);
                    }
                }

                if (groupParamCount < templateParamCount) {
                    const missingParams = templateParamCount - groupParamCount;
                    setParameterMismatch({
                        groupParams: groupParamCount,
                        groupHeaderParams: groupHeaderParamCount,
                        groupBodyParams: groupBodyParamCount,
                        templateParams: templateParamCount,
                        missing: missingParams,
                    });
                    // Initialize common parameters array for missing params
                    setCommonParameters(new Array(missingParams).fill(""));
                    
                    // If group has 0 parameters, default to custom parameters
                    if (groupParamCount === 0) {
                        if (onParameterSourceChange) {
                            onParameterSourceChange("custom");
                        } else {
                            setLocalParameterSource("custom");
                        }
                    }
                } else {
                    setParameterMismatch(null);
                }
            } else {
                notify("Failed to fetch group data", "error");
            }
        } catch (error) {
            console.error("Error fetching group data:", error);
            notify(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Failed to fetch group data",
                "error"
            );
        } finally {
            setLoadingGroupData(false);
        }
    };

    const handleCommonParameterChange = (index, value) => {
        const newParams = [...commonParameters];
        newParams[index] = value;
        setCommonParameters(newParams);
    };

    const handleCustomParameterChange = (index, value) => {
        const newParams = [...currentCustomParameters];
        newParams[index] = value;
        if (onCustomParametersChange) {
            onCustomParametersChange(newParams);
        } else {
            setLocalCustomParameters(newParams);
        }
    };

    const handleParameterSourceChange = (event, newSource) => {
        if (newSource !== null) {
            if (onParameterSourceChange) {
                onParameterSourceChange(newSource);
            } else {
                setLocalParameterSource(newSource);
            }
        }
    };

    const handleUseCommonParameters = () => {
        // Store common parameters (you may want to pass this to parent)
        notify("Common parameters will be used for all contacts", "success");
        setShowParameterDialog(false);
        setParameterMismatch(null);
    };

    const handleCreateNewTemplate = () => {
        navigate("/whatsapp-business/templates/create");
    };

    const handleCreateNewGroup = () => {
        navigate("/whatsapp-business/groups");
    };

    const handleGroupChange = (event) => {
        const groupName = event.target.value;
        onGroupSelect(groupName);
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                Select Target Audience (Group)
            </Typography>

            {groups.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No groups found. Please create a group first from "Manage Groups".
                </Alert>
            ) : (
                <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Select Group</InputLabel>
                        <Select
                            value={selectedGroup || ""}
                            label="Select Group"
                            onChange={handleGroupChange}
                        >
                            {groups.map((group, index) => (
                                <MenuItem key={index} value={group.group_name}>
                                    {group.group_name} ({group.contact_count} contacts)
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Loading Group Data */}
                    {loadingGroupData && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Loading group data...</Typography>
                        </Box>
                    )}

                    {/* Group and Template Information */}
                    {groupData && templateDetails && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 3,
                                mt: 2,
                                mb: 2,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: parameterMismatch ? "warning.main" : "success.main",
                            }}
                        >
                            <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                                Campaign Information
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Group Information */}
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 1,
                                            bgcolor: "primary.light",
                                            color: "primary.contrastText",
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, opacity: 0.9 }}
                                        >
                                            Group Information
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                                            {groupData.group_name}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    Total Contacts:
                                                </Typography>
                                                <Chip
                                                    label={groupData.total_contacts || 0}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "white",
                                                        color: "primary.main",
                                                        fontWeight: "bold",
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    Parameters in Group:
                                                </Typography>
                                                <Chip
                                                    label={groupData.number_of_parameters || 0}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "white",
                                                        color: "primary.main",
                                                        fontWeight: "bold",
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    Header: {groupData.header_param_count || 0}, Body: {groupData.body_param_count || 0}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Template Information */}
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 1,
                                            bgcolor: "info.light",
                                            color: "info.contrastText",
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, opacity: 0.9 }}
                                        >
                                            Template Information
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                            {templateDetails.name || "N/A"}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    Parameters Required:
                                                </Typography>
                                                <Chip
                                                    label={
                                                        getTotalParameterCount(templateDetails) || 0
                                                    }
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "white",
                                                        color: "info.main",
                                                        fontWeight: "bold",
                                                    }}
                                                />
                                            </Box>
                                            {(() => {
                                                const paramAnalysis = analyzeTemplateParameters(templateDetails);
                                                const templateHeaderCount = paramAnalysis.header.count || 0;
                                                const templateBodyCount = paramAnalysis.body.count || 0;
                                                return (templateHeaderCount > 0 || templateBodyCount > 0) ? (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                            Header: {templateHeaderCount}, Body: {templateBodyCount}
                                                        </Typography>
                                                    </Box>
                                                ) : null;
                                            })()}
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                    Language:
                                                </Typography>
                                                <Chip
                                                    label={templateDetails.language || "N/A"}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "white",
                                                        color: "info.main",
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Parameter Source Selection */}
                                <Grid item xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 3,
                                            bgcolor: "background.paper",
                                            border: "2px solid",
                                            borderColor: "primary.main",
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{ ...WB_SX.sectionTitle, mb: 2 }}
                                        >
                                            Parameter Source Selection
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Choose whether to use parameters from the group or provide
                                            your own custom parameters for all contacts.
                                        </Typography>

                                        {groupData.number_of_parameters === 0 ? (
                                            <Alert severity="info" sx={{ mb: 3 }}>
                                                <Typography variant="body2" fontWeight="bold">
                                                    This group has no parameters.
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    You must provide custom parameters for all contacts.
                                                </Typography>
                                            </Alert>
                                        ) : (
                                            <ToggleButtonGroup
                                                value={currentParameterSource}
                                                exclusive
                                                onChange={handleParameterSourceChange}
                                                fullWidth
                                                sx={{ mb: 3 }}
                                            >
                                                <ToggleButton
                                                    value="group"
                                                    sx={{
                                                        py: 2,
                                                        border: currentParameterSource === "group" ? 2 : 1,
                                                        borderColor:
                                                            currentParameterSource === "group"
                                                                ? "primary.main"
                                                                : "divider",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {currentParameterSource === "group" ? (
                                                            <RadioButtonChecked color="primary" />
                                                        ) : (
                                                            <RadioButtonUnchecked />
                                                        )}
                                                        <Typography variant="body1" fontWeight="bold">
                                                            Use Group Parameters
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {groupData.number_of_parameters || 0}{" "}
                                                            parameter(s) from group
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontSize: "0.7rem", mt: 0.5 }}>
                                                            Header: {groupData.header_param_count || 0}, Body: {groupData.body_param_count || 0}
                                                        </Typography>
                                                    </Box>
                                                </ToggleButton>
                                                <ToggleButton
                                                    value="custom"
                                                    sx={{
                                                        py: 2,
                                                        border: currentParameterSource === "custom" ? 2 : 1,
                                                        borderColor:
                                                            currentParameterSource === "custom"
                                                                ? "primary.main"
                                                                : "divider",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {currentParameterSource === "custom" ? (
                                                            <RadioButtonChecked color="primary" />
                                                        ) : (
                                                            <RadioButtonUnchecked />
                                                        )}
                                                        <Typography variant="body1" fontWeight="bold">
                                                            Use Custom Parameters
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            Provide your own parameter values
                                                        </Typography>
                                                    </Box>
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        )}

                                        {/* Custom Parameters Input */}
                                        {currentParameterSource === "custom" && (
                                            <Box sx={{ mt: 3 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                    sx={{ mb: 2 }}
                                                >
                                                    Enter Custom Parameters
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2 }}
                                                >
                                                    These values will be used for all contacts in the
                                                    group.
                                                </Typography>
                                                <Stack spacing={2}>
                                                    {(() => {
                                                        // Analyze template to determine parameter types
                                                        const paramAnalysis = analyzeTemplateParameters(templateDetails);
                                                        const headerParamCount = paramAnalysis.header.count || 0;
                                                        
                                                        return currentCustomParameters.map((param, index) => {
                                                            // Determine if this parameter is for header or body
                                                            let parameterType = "BODY";
                                                            let paramNumberInComponent = index + 1;
                                                            
                                                            if (index < headerParamCount) {
                                                                parameterType = "HEADER";
                                                                paramNumberInComponent = index + 1;
                                                            } else {
                                                                parameterType = "BODY";
                                                                paramNumberInComponent = index - headerParamCount + 1;
                                                            }
                                                            
                                                            return (
                                                                <Box key={index} sx={{ position: "relative" }}>
                                                                    <TextField
                                                                        fullWidth
                                                                        label={`Parameter ${index + 1} ({{${index + 1}}})`}
                                                                        value={param || ""}
                                                                        onChange={(e) =>
                                                                            handleCustomParameterChange(
                                                                                index,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        helperText={
                                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                                                                <Chip
                                                                                    label={parameterType}
                                                                                    size="small"
                                                                                    color={parameterType === "HEADER" ? "primary" : "secondary"}
                                                                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                                                                />
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    Value for {parameterType.toLowerCase()} parameter {`{{${paramNumberInComponent}}}`}
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                        required
                                                                    />
                                                                </Box>
                                                            );
                                                        });
                                                    })()}
                                                </Stack>
                                            </Box>
                                        )}

                                        {/* Group Parameters Info */}
                                        {currentParameterSource === "group" && (
                                            <Box sx={{ mt: 3 }}>
                                                <Alert severity="info" icon={<Info />}>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                                        Using parameters from the group
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                                        Each contact will use their own parameter values from the group data.
                                                    </Typography>
                                                    {groupData.header_param_count !== undefined || groupData.body_param_count !== undefined ? (
                                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                                            <strong>Parameter breakdown:</strong> Header: {groupData.header_param_count || 0}, Body: {groupData.body_param_count || 0}
                                                        </Typography>
                                                    ) : null}
                                                </Alert>
                                            </Box>
                                        )}

                                        {/* Parameter Mismatch Warning */}
                                        {parameterMismatch &&
                                            currentParameterSource === "group" &&
                                            groupData.number_of_parameters > 0 && (
                                                <Alert severity="warning" sx={{ mt: 3 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        Parameter Mismatch Detected
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                                        The group has {parameterMismatch.groupParams}{" "}
                                                        parameters, but the template requires{" "}
                                                        {parameterMismatch.templateParams} parameters.
                                                        Missing {parameterMismatch.missing} parameter(s).
                                                    </Typography>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                                        Options to resolve:
                                                    </Typography>
                                                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2 }}>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<Create />}
                                                            onClick={handleCreateNewTemplate}
                                                            size="small"
                                                            sx={WB_SX.secondaryButton}
                                                        >
                                                            Create New Template ({parameterMismatch.groupParams}{" "}
                                                            params)
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<GroupAdd />}
                                                            onClick={handleCreateNewGroup}
                                                            size="small"
                                                            sx={WB_SX.secondaryButton}
                                                        >
                                                            Create New Group ({parameterMismatch.templateParams}{" "}
                                                            params)
                                                        </Button>
                                </Box>
                                    </Alert>
                                )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
};

export default GroupSelectionStep;

